import React, { useEffect, useState } from 'react';
import { LogBox, Platform } from 'react-native';
import { loadFonts } from './src/utils/fonts';
import { AppNavigator } from './src/navigation/AppNavigator';
import { CallScreen } from './src/screens/CallScreen';
import { PhoneDialerRoot } from './src/components/PhoneDialerRoot';
import { CalendarReminderAlertRoot } from './src/components/CalendarReminderAlertRoot';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { LocaleProvider } from './src/contexts/LocaleContext';
import { ThemeProvider } from './src/contexts/ThemeContext';
import { colors } from './src/theme/colors';
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity } from 'react-native';

// /call is a standalone, pre-auth, query-string-driven route meant to be
// loaded inside a native WebView (see CallScreen.tsx's own doc comment) —
// it authenticates itself entirely from its own URL's ?token= param, unlike
// every other screen in this app, which all assume a session already
// restored from local storage before AppNavigator's own auth-gated router
// ever runs. Intercepted here, before AppNavigator mounts at all, rather
// than threaded through the existing currentScreen/routeParams machinery —
// that router only ever extracts path SEGMENTS (window.location.pathname),
// never the query string, and assumes isAuthenticated is already true by
// the time any deep link resolves; retrofitting both would risk the
// existing auth flow for every other screen just to fit one narrow case.
const isCallRoute = Platform.OS === 'web' && typeof window !== 'undefined' && window.location.pathname === '/call';

// Disable the console log panel on mobile simulators (hides the bottom black dev panel)
// Logs will still work in the console/debugger, but won't appear in the UI
if (typeof LogBox !== 'undefined') {
  LogBox.ignoreAllLogs();
}

export default function App() {
  const [fontsReady, setFontsReady] = useState(false);
  const [updateState, setUpdateState] = useState<'checking' | 'updating' | 'ready'>('ready');

  useEffect(() => {
    loadFonts()
      .catch((error) => console.error('❌ [App] Font loading failed:', error))
      .finally(() => setFontsReady(true));
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web') return;
    checkForUpdate();
  }, []);

  const checkForUpdate = async () => {
    try {
      const Updates = await import('expo-updates');
      if (Updates.isEmbeddedLaunch) return; // native binary launch, updates not active yet
      setUpdateState('checking');
      const result = await Updates.checkForUpdateAsync();
      if (result.isAvailable) {
        setUpdateState('updating');
        await Updates.fetchUpdateAsync();
        await Updates.reloadAsync();
      } else {
        setUpdateState('ready');
      }
    } catch {
      setUpdateState('ready');
    }
  };

  if (!fontsReady) {
    return (
      <View style={styles.updateContainer}>
        <ActivityIndicator size="large" color={colors.primary as string} />
      </View>
    );
  }

  // Short-circuits everything else (update check, ThemeProvider/LocaleProvider,
  // AppNavigator's own auth-gated boot sequence) — CallScreen brings its own
  // minimal providers and authenticates purely from the URL, see its own
  // comment and the isCallRoute comment above.
  if (isCallRoute) {
    return (
      <SafeAreaProvider>
        <ThemeProvider>
          <LocaleProvider>
            <CallScreen />
            {/* usePhoneDialer (inside CallScreen) mounts InnoCalls' widget
                into '#innocalls-dialer-root' — must already exist in the DOM
                before that hook's init effect runs, same requirement as the
                normal app boot in AppNavigator's own tree below. */}
            <PhoneDialerRoot />
          </LocaleProvider>
        </ThemeProvider>
      </SafeAreaProvider>
    );
  }

  if (updateState === 'checking' || updateState === 'updating') {
    return (
      <View style={styles.updateContainer}>
        <ActivityIndicator size="large" color={colors.primary as string} />
        <Text style={styles.updateText}>
          {updateState === 'updating' ? 'Applying update…' : 'Checking for updates…'}
        </Text>
      </View>
    );
  }

  try {
    console.log('🔍 [App] Starting app initialization...');

    // Use unified AppNavigator for both web and mobile
    return (
      <SafeAreaProvider>
        <ThemeProvider>
          <LocaleProvider>
            <AppNavigator />
            <PhoneDialerRoot />
            <CalendarReminderAlertRoot />
          </LocaleProvider>
        </ThemeProvider>
      </SafeAreaProvider>
    );
  } catch (error) {
    console.error('❌ [App] Critical error during app initialization:', error);

    // Show error screen instead of crashing
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorTitle}>App Error</Text>
        <Text style={styles.errorMessage}>
          The app encountered an error during startup. Please restart the app.
        </Text>
        <Text style={styles.errorDetails}>
          Error: {(error as Error).message}
        </Text>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  updateContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
    gap: 16,
  },
  updateText: {
    fontSize: 16,
    color: '#666',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#f5f5f5',
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#d32f2f',
    marginBottom: 16,
  },
  errorMessage: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 16,
  },
  errorDetails: {
    fontSize: 12,
    color: '#999',
    textAlign: 'center',
    fontFamily: 'monospace',
  },
});
